/**
 * Hook for subscribing to and paginating view history events.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { HistoryEvent, HistoryEventType } from '@/types/history';
import { subscribeToViewHistory, loadMoreHistory } from '@/services/historyService';

const PAGE_SIZE = 50;

export function useViewHistory(
  userId: string | undefined,
  projectId: string | undefined,
  viewId: string | undefined,
  filterType: HistoryEventType | 'all'
) {
  const [events, setEvents] = useState<HistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const unsubRef = useRef<(() => void) | null>(null);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!userId || !projectId || !viewId) {
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasMore(true);

    // Cleanup previous subscription
    if (unsubRef.current) {
      unsubRef.current();
    }

    const unsub = subscribeToViewHistory(userId, projectId, viewId, {
      pageSize: PAGE_SIZE,
      filterType: filterType === 'all' ? undefined : filterType,
      onUpdate: (newEvents) => {
        setEvents(newEvents);
        setHasMore(newEvents.length >= PAGE_SIZE);
        setLoading(false);
      },
      onError: () => {
        setEvents([]);
        setHasMore(false);
        setLoading(false);
      },
    });

    unsubRef.current = unsub;

    return () => {
      unsub();
      unsubRef.current = null;
    };
  }, [userId, projectId, viewId, filterType]);

  const loadMore = useCallback(async () => {
    if (!userId || !projectId || !viewId || loadingMore || !hasMore || events.length === 0) return;

    const lastEvent = events[events.length - 1];
    if (!lastEvent?.timestamp) return;

    setLoadingMore(true);
    try {
      const older = await loadMoreHistory(userId, projectId, viewId, lastEvent.timestamp, {
        pageSize: PAGE_SIZE,
        filterType: filterType === 'all' ? undefined : filterType,
      });
      setEvents((prev) => [...prev, ...older]);
      setHasMore(older.length >= PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  }, [userId, projectId, viewId, filterType, loadingMore, hasMore, events]);

  return { events, loading, hasMore, loadMore, loadingMore };
}
